import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Leaf, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';

const questions = [
  {
    id: 1,
    question: "How would you describe your land's water situation?",
    options: [
      { text: "Water scarcity - dry most of the year", score: 1, category: "water" },
      { text: "Seasonal - good during monsoon, dry otherwise", score: 2, category: "water" },
      { text: "Adequate - some natural water sources", score: 3, category: "water" },
      { text: "Abundant - streams, springs, or high water table", score: 4, category: "water" }
    ]
  },
  {
    id: 2,
    question: "What is the current vegetation on your land?",
    options: [
      { text: "Bare or degraded - mostly weeds and sparse cover", score: 1, category: "vegetation" },
      { text: "Grassland or scrub with few trees", score: 2, category: "vegetation" },
      { text: "Mixed vegetation with some mature trees", score: 3, category: "vegetation" },
      { text: "Dense forest or established food forest", score: 4, category: "vegetation" }
    ]
  },
  {
    id: 3,
    question: "How would you describe your soil condition?",
    options: [
      { text: "Compacted, eroded, or very rocky", score: 1, category: "soil" },
      { text: "Sandy or clayey with low organic matter", score: 2, category: "soil" },
      { text: "Moderate fertility with some organic content", score: 3, category: "soil" },
      { text: "Rich, dark soil with good structure", score: 4, category: "soil" }
    ]
  },
  {
    id: 4,
    question: "What is the slope/terrain of your land?",
    options: [
      { text: "Steep slopes (>30%) with erosion risk", score: 1, category: "terrain" },
      { text: "Moderate slopes (15-30%)", score: 2, category: "terrain" },
      { text: "Gentle slopes (5-15%)", score: 3, category: "terrain" },
      { text: "Mostly flat or well-contoured", score: 4, category: "terrain" }
    ]
  },
  {
    id: 5,
    question: "What is your primary goal for the land?",
    options: [
      { text: "Ecological restoration and wildlife habitat", score: 0, category: "goal", tag: "restoration" },
      { text: "Food production for family/community", score: 0, category: "goal", tag: "food" },
      { text: "Commercial farming or agroforestry", score: 0, category: "goal", tag: "commercial" },
      { text: "Retreat/homestead with mixed use", score: 0, category: "goal", tag: "homestead" }
    ]
  }
];

const profiles = {
  high: {
    title: "Thriving Ecosystem",
    description: "Your land shows excellent ecological health! Focus on enhancement and diversification.",
    recommendations: [
      "Introduce rare native species",
      "Create wildlife corridors",
      "Establish seed saving programs",
      "Consider hosting educational workshops"
    ],
    color: "#1A4D2E"
  },
  medium: {
    title: "Growing Potential",
    description: "Your land has good foundations with room for improvement in key areas.",
    recommendations: [
      "Implement water harvesting systems",
      "Build soil through composting and mulching",
      "Plant pioneer species for succession",
      "Create swales on contour"
    ],
    color: "#2d7a4a"
  },
  low: {
    title: "Restoration Ready",
    description: "Your land needs regenerative intervention. With the right strategies, transformation is possible.",
    recommendations: [
      "Start with water retention earthworks",
      "Plant nitrogen-fixing pioneer trees",
      "Add organic matter aggressively",
      "Protect from erosion immediately"
    ],
    color: "#A0522D"
  }
};

export const LandHealthQuiz = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleAnswer = (option) => {
    setSelectedOption(option);
  };

  const handleNext = () => {
    if (selectedOption === null) return;
    
    const newAnswers = [...answers, selectedOption];
    setAnswers(newAnswers);
    setSelectedOption(null);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(answers[currentQuestion - 1] || null);
      setAnswers(answers.slice(0, -1));
    }
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setSelectedOption(null);
  };

  const calculateScore = () => {
    const totalScore = answers.reduce((sum, ans) => sum + (ans.score || 0), 0);
    const maxScore = 16; // 4 questions * 4 max points
    const percentage = (totalScore / maxScore) * 100;
    return { totalScore, percentage };
  };

  const getProfile = () => {
    const { percentage } = calculateScore();
    if (percentage >= 70) return profiles.high;
    if (percentage >= 40) return profiles.medium;
    return profiles.low;
  };

  const getCategoryScores = () => {
    const categories = {};
    answers.forEach(ans => {
      if (ans.category !== 'goal' && ans.score) {
        categories[ans.category] = ans.score;
      }
    });
    return categories;
  };

  if (showResult) {
    const { percentage } = calculateScore();
    const profile = getProfile();
    const categoryScores = getCategoryScores();
    const goal = answers.find(a => a.category === 'goal');

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto"
        data-testid="quiz-result"
      >
        <div className="text-center mb-8">
          <h3 className="font-serif text-2xl text-forest mb-2">Your Land Health Profile</h3>
          <p className="text-foreground/60 text-sm">Based on your responses</p>
        </div>

        {/* Score Ring */}
        <div className="flex justify-center mb-8">
          <div className="relative w-48 h-48">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              {/* Background ring */}
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#E8E6E1"
                strokeWidth="8"
              />
              {/* Score ring */}
              <motion.circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={profile.color}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 40}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 40 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 40 * (1 - percentage / 100) }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span
                className="text-4xl font-bold"
                style={{ color: profile.color }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {Math.round(percentage)}%
              </motion.span>
              <span className="text-sm text-foreground/50">Health Score</span>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center mb-8"
        >
          <h4 className="font-serif text-xl mb-2" style={{ color: profile.color }}>
            {profile.title}
          </h4>
          <p className="text-foreground/70">{profile.description}</p>
          {goal && (
            <p className="text-sm text-earth mt-2">
              Goal: {goal.text}
            </p>
          )}
        </motion.div>

        {/* Category Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="grid grid-cols-2 gap-4 mb-8"
        >
          {Object.entries(categoryScores).map(([category, score], idx) => (
            <div key={category} className="bg-sand rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium capitalize text-forest">{category}</span>
                <span className="text-xs text-foreground/50">{score}/4</span>
              </div>
              <div className="h-2 bg-border/30 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: profile.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${(score / 4) * 100}%` }}
                  transition={{ delay: 1.2 + idx * 0.1, duration: 0.5 }}
                />
              </div>
            </div>
          ))}
        </motion.div>

        {/* Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="mb-8"
        >
          <h4 className="font-medium text-forest mb-4">Recommended Next Steps:</h4>
          <ul className="space-y-2">
            {profile.recommendations.map((rec, idx) => (
              <motion.li
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.6 + idx * 0.1 }}
                className="flex items-start gap-3 text-sm text-foreground/70"
              >
                <Leaf className="w-4 h-4 text-forest flex-shrink-0 mt-0.5" />
                {rec}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1 btn-outline"
            data-testid="quiz-reset"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Retake Quiz
          </Button>
          <Button
            onClick={() => onComplete && onComplete()}
            className="flex-1 btn-primary"
            data-testid="quiz-contact"
          >
            Get Expert Assessment
          </Button>
        </div>
      </motion.div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto"
      data-testid="land-health-quiz"
    >
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-foreground/50 mb-2">
          <span>Question {currentQuestion + 1} of {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-sand rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-forest rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="font-serif text-xl text-forest mb-6">{question.question}</h3>
          
          <div className="space-y-3">
            {question.options.map((option, idx) => (
              <motion.button
                key={idx}
                onClick={() => handleAnswer(option)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                  selectedOption === option
                    ? 'border-forest bg-forest/5'
                    : 'border-border/50 hover:border-forest/30 hover:bg-sand/50'
                }`}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                data-testid={`quiz-option-${idx}`}
              >
                <span className={`text-sm ${
                  selectedOption === option ? 'text-forest font-medium' : 'text-foreground/70'
                }`}>
                  {option.text}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="btn-outline"
          data-testid="quiz-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={selectedOption === null}
          className="btn-primary"
          data-testid="quiz-next"
        >
          {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </motion.div>
  );
};
